import posthog from 'posthog-js';
import { databaseService } from './DatabaseService';
// Import functions directly from the file path without extension
import { encryptData, decryptData, isCryptoSupported } from '../utils/cryptoUtils';
import { pouchDBService } from './PouchDBService';
import {
  MedicationData,
  MedicationAdherenceData,
  MedicationSchedulePattern,
  extractMedicationPatterns,
  meetsKAnonymityRequirements
} from '../utils/localDataProcessing';

// Types
interface PostHogOptions {
  apiKey: string;
  host?: string;
  apiHost?: string; // Alternative name for host to support existing code
  debug?: boolean;
  capturePageview?: boolean;
  autocapture?: boolean;
}

// Data contribution preferences
interface DataContributionPreferences {
  enabled: boolean; // Master toggle for opting in/out
  medicationPatterns: boolean; // Sharing medication scheduling patterns
  adherenceData: boolean; // Sharing anonymized adherence statistics
  contextualFactors: boolean; // Sharing anonymized contextual factors
}

interface UserProperties {
  isPlusTier?: boolean; // Consistent Plus tier naming (not Pro)
  deviceType?: string;
  viewPreference?: string;
  lastActive?: string;
  [key: string]: any;
}

/**
 * PostHog Analytics Service
 * Provides a consistent interface for tracking events, managing user identity,
 * and ensuring privacy compliance throughout the application
 */
class PostHogService {
  private initialized = false;
  private consentGiven = false;
  private userId: string | null = null;
  private puid: string | null = null; // Pseudonymous User ID for privacy-safe analytics
  private readonly PUID_STORAGE_KEY = 'meduler_puid_v1'; // Storage key for encrypted PUID
  private readonly CONSENT_STORAGE_KEY = 'analytics_consent';
  private readonly DATA_CONTRIBUTION_PREFS_KEY = 'data_contribution_preferences';
  private dataContributionPrefs: DataContributionPreferences = {
    enabled: false,
    medicationPatterns: false,
    adherenceData: false,
    contextualFactors: false
  };
  private readonly K_ANONYMITY_THRESHOLD = 5; // As specified in the PRD

  /**
   * Initialize PostHog with your project's API key
   * Call this once at application startup, typically in App.tsx
   */
  async initialize(options: PostHogOptions): Promise<void> {
    // Initialize PouchDB service for offline support
    await pouchDBService.initialize();
    if (this.initialized) {
      return;
    }
    
    // Load data contribution preferences
    await this.loadDataContributionPreferences();

    // Check if we're using a development/dummy key
    const isDevelopmentKey = options.apiKey === 'dummy_key_for_development' || !options.apiKey;
    
    // In development mode, create a mock PostHog instance to prevent API errors
    if (isDevelopmentKey) {
      
      this.initialized = true;
      return;
    }

    try {
      // Initialize PostHog client
      posthog.init(options.apiKey, {
        api_host: options.host || 'https://app.posthog.com',
        autocapture: false, // Disable automatic event capture for GDPR compliance
        capture_pageview: false, // We'll manually track pageviews
        debug: false, // Force debug mode off to reduce console logs
        persistence: 'localStorage+cookie', // For better cross-device tracking
        disable_session_recording: true, // Disable by default, enable only with explicit consent
        loaded: (posthog) => {
          // Disable automatic capturing for GDPR compliance
          posthog.config.capture_performance = false;
        }
      });
    } catch (error) {
      // Silently handle initialization errors to prevent app crashes
      // Log error in development environment only
      if (process.env.NODE_ENV === 'development') {
        console.error('PostHog initialization failed:', error);
      }
      // Mark as uninitialized so other methods can gracefully handle the failure
      this.initialized = false;
    }

    // Check for previous consent and PUID
    this.loadConsentStatus();
    await this.loadExistingPUID();
    
    // Check for offline events to send
    this.processOfflineEvents();
    
    // Schedule periodic local data processing if user has opted in
    if (this.dataContributionPrefs.enabled) {
      this.scheduleLocalDataProcessing();
    }
    
    this.initialized = true;
  }

  /**
   * Set user identity for tracking after authentication
   * This should be called after a user logs in or profile is loaded
   */
  async identifyUser(userId: string, userProperties?: UserProperties): Promise<void> {
    if (!this.initialized || !this.consentGiven) {
      return; // Don't identify users without initialization or consent
    }
    
    // Generate a pseudonymous user ID (PUID) using standard UUID v4 format
    // This provides better privacy protection than hashing the real user ID
    this.puid = await this.generateOrRetrievePUID(userId);
    
    // Store the PUID securely using encryption
    await this.securelySavePUID();
    
    // Only proceed with identification if we have a valid PUID
    if (!this.puid) {
      
      return;
    }

    // Standardize property naming to ensure Plus tier consistency
    let standardizedProperties: Record<string, any> = {};
    
    if (userProperties) {
      // Convert any "pro" related keys to "plus" format
      standardizedProperties = { ...userProperties };
      Object.keys(standardizedProperties).forEach(key => {
        if (key.toLowerCase().includes('pro') && !key.toLowerCase().includes('profile')) {
          const newKey = key.replace(/pro/gi, 'plus');
          standardizedProperties[newKey] = standardizedProperties[key];
          delete standardizedProperties[key];
        }

        // Convert string values from Pro to Plus
        if (typeof standardizedProperties[key] === 'string' && 
            standardizedProperties[key].includes('Pro') &&
            !standardizedProperties[key].includes('Profile')) {
          standardizedProperties[key] = standardizedProperties[key].replace(/Pro/g, 'Plus');
        }
      });
    }
    
    // Always add device type and user agent for better analytics
    standardizedProperties.device_type = this.getDeviceType();
    standardizedProperties.platform = this.getPlatform();
    
    // Following PostHog documentation for identify method
    // https://posthog.com/docs/product-analytics/identify
    try {
      posthog.identify(
        this.puid, // Use PUID as the distinct_id for privacy-safe analytics
        standardizedProperties // Pass standardized properties with Plus naming convention
      );
      
    } catch (error) {
      // Silently handle initialization errors to prevent app crashes
      // Log error in development environment only
      if (process.env.NODE_ENV === 'development') {
        console.error('PostHog initialization failed:', error);
      }
      // Mark as uninitialized so other methods can gracefully handle the failure
      this.initialized = false;
    }

    this.userId = userId;
    
    // Save user ID for consistency across devices
    await this.saveUserInfo();
  }

  /**
   * Track specific events with custom properties
   */
  async trackEvent(eventName: string, properties?: Record<string, any>): Promise<void> {
    if (!this.initialized || !this.consentGiven) {
      return; // Don't track events without consent
    }
    
    // Don't track if we don't have a PUID
    if (!this.puid) {
      
      return;
    }

    // Standardize any property values that might contain "Pro" to use "Plus"
    const standardizedProperties = this.standardizeProperties(properties || {});
    
    // Always include PUID with events for consistent user journeys
    standardizedProperties.$distinct_id = this.puid;

    try {
      // Check if we're online
      const isOnline = navigator.onLine;
      
      if (isOnline && typeof posthog.capture === 'function') {
        // If online, track the event with PostHog
        posthog.capture(eventName, standardizedProperties);
      } else {
        // If offline, queue the event for later sending
        
      }
    } catch (error) {
      // Silently handle initialization errors to prevent app crashes
      // Log error in development environment only
      if (process.env.NODE_ENV === 'development') {
        console.error('PostHog initialization failed:', error);
      }
      // Mark as uninitialized so other methods can gracefully handle the failure
      this.initialized = false;
    }

    // Always save event data locally for offline support
    await this.saveEventLocally(eventName, standardizedProperties);
  }

  /**
   * Track page views with optional metadata
   */
  async trackPageView(pageName: string, properties?: Record<string, any>): Promise<void> {
    const standardizedProperties = this.standardizeProperties({
      pageName,
      ...properties
    });

    await this.trackEvent('$pageview', standardizedProperties);
  }

  /**
   * Set user consent for tracking (GDPR compliant)
   */
  setConsent(hasConsent: boolean): void {
    this.consentGiven = hasConsent;
    localStorage.setItem('analytics_consent', hasConsent ? 'true' : 'false');
    
    if (hasConsent) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
      // Clear cookies on opt-out
      document.cookie.split(';').forEach(cookie => {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie) {
          // Extract the cookie name safely
          const nameMatch = trimmedCookie.match(/^([^=]+)/); 
          const name = nameMatch && nameMatch[1] ? nameMatch[1].trim() : '';
          
          // Only proceed if we have a valid name
          if (name && (name.includes('ph_') || name.includes('posthog'))) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          }
        }
      });
    }
  }

  /**
   * Check if user has given consent
   */
  hasConsent(): boolean {
    return this.consentGiven;
  }

  /**
   * Get the current user ID being tracked
   */
  getCurrentUserId(): string | null {
    return this.userId;
  }

  /**
   * Get the current pseudonymous user ID (PUID) being used for analytics
   */
  getCurrentPUID(): string | null {
    return this.puid;
  }

  /**
   * Reset current user (use on logout)
   */
  reset(): void {
    this.userId = null;
    this.puid = null;
    if (this.initialized) {
      posthog.reset();
    }
  }

  /**
   * Load consent status from local storage
   */
  private loadConsentStatus(): void {
    const consent = localStorage.getItem('analytics_consent');
    this.consentGiven = consent === 'true';
    
    if (this.consentGiven) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  }

  /**
   * Standardize properties to ensure Plus tier naming convention
   */
  private standardizeProperties(properties: Record<string, any>): Record<string, any> {
    const result = { ...properties };
    
    // Handle string values
    Object.keys(result).forEach(key => {
      // Replace Pro with Plus in keys (except for profile-related keys)
      if (key.toLowerCase().includes('pro') && !key.toLowerCase().includes('profile')) {
        const newKey = key.replace(/pro/gi, 'plus');
        result[newKey] = result[key];
        delete result[key];
      }
      
      // Replace Pro with Plus in string values
      if (typeof result[key] === 'string') {
        result[key] = result[key].replace(/Pro(?!file)/g, 'Plus');
      }
      
      // Handle nested objects
      if (result[key] && typeof result[key] === 'object' && !Array.isArray(result[key])) {
        result[key] = this.standardizeProperties(result[key]);
      }
    });
    
    return result;
  }

  /**
   * Save user info in local database for offline tracking consistency
   */
  private async saveUserInfo(): Promise<void> {
    if (!this.userId || !this.puid) return;
    
    try {
      // Store in both databaseService and PouchDB for redundancy
      await databaseService.saveData('analytics_meta', 'user_identity', {
        userId: this.userId,
        puid: this.puid,
        lastIdentified: new Date().toISOString()
      });
      
      // Also store in PouchDB for offline support
      await pouchDBService.storeUserMapping(this.userId, this.puid);
    } catch (err) {
      // Silent catch - analytics should never break the app
      
    }
  }

  /**
   * Save event data locally for possible offline tracking or sync
   */
  private async saveEventLocally(eventName: string, properties: Record<string, any>): Promise<void> {
    if (!this.puid) return;
    
    // Only save important events locally to avoid excessive storage
    const criticalEvents = [
      'medication_added',
      'medication_taken',
      'medication_skipped',
      'profile_created',
      'profile_switched',
      'plus_subscription_started', // Using correct "Plus" naming for premium tier
      'plus_subscription_renewed',
      'plus_subscription_canceled'
    ];
    
    // Always save pageviews and critical events
    if (eventName !== '$pageview' && !criticalEvents.includes(eventName)) {
      return; // Skip non-critical events
    }
    
    try {
      // Store in regular DB
      await databaseService.saveData('analytics_events', `event_${Date.now()}`, {
        name: eventName,
        properties,
        timestamp: new Date().toISOString(),
        puid: this.puid, // Use PUID instead of actual user ID for privacy
        userId: null // Don't store actual user ID with events
      });
      
      // Also store in PouchDB for offline support and syncing
      await pouchDBService.queueAnalyticsEvent(eventName, properties, this.puid);
    } catch (error) {
      // Silent catch - analytics should never break the app
      
    }
  }

  /**
   * Get device type for analytics
   */
  private getDeviceType(): string {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      return 'tablet';
    }
    if (/Mobile|iPhone|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      return 'mobile';
    }
    return 'desktop';
  }
  
  /**
   * Get platform information for analytics
   */
  private getPlatform(): string {
    const userAgent = navigator.userAgent || '';
    if (/Windows/i.test(userAgent)) return 'windows';
    if (/Macintosh|Mac OS X/i.test(userAgent)) return 'mac';
    if (/Android/i.test(userAgent)) return 'android';
    if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
    if (/Linux/i.test(userAgent)) return 'linux';
    return 'unknown';
  }

  /**
   * Generate a new standard UUID v4 or retrieve a previously stored one
   * This provides full compliance with the PUID system requirements
   * @param userId Associated user ID (not used in generation, only for mapping)
   * @returns A standard UUID v4 for privacy-safe analytics
   */
  private async generateOrRetrievePUID(userId: string): Promise<string> {
    // Try to retrieve an existing PUID from secure storage first
    const existingPUID = await this.retrieveSecurePUID();
    if (existingPUID) {
      
      return existingPUID;
    }
    
    // Generate a new standard UUID v4 using Web Crypto API when available
    let uuid = '';
    
    // Use crypto.randomUUID() if available (modern browsers)
    if (typeof window !== 'undefined' && window.crypto && 'randomUUID' in window.crypto) {
      // TypeScript doesn't recognize randomUUID on the Crypto interface yet in some versions
      // @ts-ignore - Modern browsers support this
      uuid = window.crypto.randomUUID();
    }
    // Fallback to manual UUID v4 generation using getRandomValues
    else if (typeof window !== 'undefined' && window.crypto && 'getRandomValues' in window.crypto) {
      uuid = this.generateUUIDv4();
    }
    // Last resort fallback
    else {
      uuid = this.generateFallbackUUID();
    }
    
    // Store user ID mapping in a separate secure location for internal reference only
    await this.storeUserIdMapping(userId, uuid);
    
    return uuid;
  }
  
  /**
   * Generate a standard UUID v4 using Web Crypto API
   * @returns A cryptographically strong UUID v4
   */
  private generateUUIDv4(): string {
    // Create an array of 16 random bytes with proper typing
    const bytes = new Uint8Array(16);
    
    try {
      // Use secure random values when available
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(bytes);
      } else {
        // Fallback for environments without crypto API
        for (let i = 0; i < 16; i++) {
          bytes[i] = Math.floor(Math.random() * 256);
        }
      }
      
      // Set the version bits to 4 (UUID v4) and variant bits correctly
      // Version 4 UUID: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where y is 8, 9, A, or B
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4 (bits: 0100xxxx)
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 1 (bits: 10xxxxxx)
    } catch (error) {
      // If there's an error, log it and use the fallback method
      console.error('Error generating UUID with crypto API:', error);
      return this.generateFallbackUUID();
    }
    
    // Convert to hex and format as standard UUID
    // Format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const hex = Array.from(bytes)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    return [
      hex.substring(0, 8),
      hex.substring(8, 12),
      hex.substring(12, 16),
      hex.substring(16, 20),
      hex.substring(20, 32)
    ].join('-');
  }
  
  /**
   * Generate a fallback UUID when crypto APIs are unavailable
   * Less secure but ensures functionality in older browsers
   */
  private generateFallbackUUID(): string {
    const pattern = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    return pattern.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  
  /**
   * Store a mapping between user ID and PUID for internal reference
   * This mapping is only stored locally and never sent to analytics
   */
  private async storeUserIdMapping(userId: string, puid: string): Promise<void> {
    try {
      // Store in both database systems for redundancy
      await databaseService.saveData('analytics_meta', 'user_mapping', {
        userId,
        puid,
        created: new Date().toISOString()
      });
      
      // Also store in PouchDB for offline support
      await pouchDBService.storeUserMapping(userId, puid);
    } catch (error) {
      // Silently handle initialization errors to prevent app crashes
      // Log error in development environment only
      if (process.env.NODE_ENV === 'development') {
        console.error('PostHog initialization failed:', error);
      }
      // Mark as uninitialized so other methods can gracefully handle the failure
      this.initialized = false;
    }
  }
  
  /**
   * Securely save the PUID using Web Crypto API encryption
   */
  private async securelySavePUID(): Promise<void> {
    if (!this.puid) return;
    
    try {
      // Encrypt the PUID before storing it
      const encryptedValue = await encryptData(this.puid);
      localStorage.setItem(this.PUID_STORAGE_KEY, encryptedValue);
      
      // Also store in PouchDB for offline resilience
      await pouchDBService.storePUID(this.puid, encryptedValue);
      
      
    } catch (error) {
      // Silently handle initialization errors to prevent app crashes
      // Log error in development environment only
      if (process.env.NODE_ENV === 'development') {
        console.error('PostHog initialization failed:', error);
      }
      // Mark as uninitialized so other methods can gracefully handle the failure
      this.initialized = false;
    }
  }
  
  /**
   * Retrieve the securely stored PUID using Web Crypto API decryption
   */
  private async retrieveSecurePUID(): Promise<string | null> {
    try {
      const encryptedValue = localStorage.getItem(this.PUID_STORAGE_KEY);
      if (!encryptedValue) return null;
      
      const decryptedValue = await decryptData(encryptedValue);
      return decryptedValue;
    } catch (error) {
      
      return null;
    }
  }
  
  /**
   * Load any existing PUID from secure storage
   */
  private async loadExistingPUID(): Promise<void> {
    // Try to load from encrypted localStorage first
    this.puid = await this.retrieveSecurePUID();
    
    // If not found in localStorage, try PouchDB
    if (!this.puid) {
      this.puid = await pouchDBService.retrievePUID();
      if (this.puid) {
        
        // Save to encrypted localStorage for faster access next time
        await this.securelySavePUID();
      }
    } else {
      
    }
  }
  
  /**
   * Set user preferences for data contribution (opt-in for aggregated insights)
   * This follows the granular control model specified in the PRD
   */
  async setDataContributionPreferences(preferences: DataContributionPreferences): Promise<void> {
    // Update preferences
    this.dataContributionPrefs = { ...preferences };
    
    // Save to localStorage
    localStorage.setItem(
      this.DATA_CONTRIBUTION_PREFS_KEY, 
      JSON.stringify(this.dataContributionPrefs)
    );
    
    // If user has opted in, schedule data processing
    if (this.dataContributionPrefs.enabled) {
      this.scheduleLocalDataProcessing();
      
      // Track opt-in event (only if general analytics consent is also given)
      if (this.consentGiven) {
        await this.trackEvent('data_contribution_opted_in', {
          medicationPatterns: this.dataContributionPrefs.medicationPatterns,
          adherenceData: this.dataContributionPrefs.adherenceData,
          contextualFactors: this.dataContributionPrefs.contextualFactors
        });
      }
    } else {
      // If user has opted out, track opt-out event
      if (this.consentGiven) {
        await this.trackEvent('data_contribution_opted_out');
      }
    }
  }
  
  /**
   * Get current data contribution preferences
   */
  getDataContributionPreferences(): DataContributionPreferences {
    return { ...this.dataContributionPrefs };
  }
  
  /**
   * Load data contribution preferences from storage
   */
  private async loadDataContributionPreferences(): Promise<void> {
    const storedPrefs = localStorage.getItem(this.DATA_CONTRIBUTION_PREFS_KEY);
    
    if (storedPrefs) {
      try {
        this.dataContributionPrefs = JSON.parse(storedPrefs);
      } catch (error) {
        // If parsing fails, use default preferences (all off)
        this.dataContributionPrefs = {
          enabled: false,
          medicationPatterns: false,
          adherenceData: false,
          contextualFactors: false
        };
      }
    }
  }
  
  /**
   * Schedule periodic local data processing for pattern extraction
   * This follows the edge computing approach described in the PRD
   */
  private scheduleLocalDataProcessing(): void {
    // Process immediately if it's been more than a day
    this.processLocalData();
    
    // Then schedule to run daily during idle time
    if ('requestIdleCallback' in window) {
      // Schedule using idle callbacks when supported
      const scheduleNextRun = () => {
        (window as any).requestIdleCallback(
          () => {
            this.processLocalData();
            // Re-schedule for tomorrow
            setTimeout(scheduleNextRun, 24 * 60 * 60 * 1000);
          },
          { timeout: 10000 } // 10 second timeout
        );
      };
      
      // Initial schedule
      setTimeout(scheduleNextRun, 24 * 60 * 60 * 1000);
    } else {
      // Fallback for browsers without idle callback
      setInterval(() => this.processLocalData(), 24 * 60 * 60 * 1000);
    }
  }
  
  /**
   * Process medication data locally to extract patterns
   * This is the core of the edge computing approach described in the PRD
   */
  private async processLocalData(): Promise<void> {
    // Skip if user hasn't opted in
    if (!this.dataContributionPrefs.enabled) {
      return;
    }
    
    try {
      // 1. Get medication data from local database
      const medications = await this.getMedicationsFromLocalStorage();
      if (!medications || medications.length === 0) {
        return; // No data to process
      }
      
      // 2. Get adherence data if user has opted in for that
      let adherenceData: MedicationAdherenceData[] = [];
      if (this.dataContributionPrefs.adherenceData) {
        adherenceData = await this.getAdherenceDataFromLocalStorage();
      }
      
      // 3. Extract patterns locally
      const pattern = await extractMedicationPatterns(medications, adherenceData);
      
      // 4. Check if pattern meets k-anonymity requirements
      const meetsRequirements = meetsKAnonymityRequirements(
        pattern, 
        this.K_ANONYMITY_THRESHOLD
      );
      
      // 5. Only contribute pattern if it meets privacy requirements
      if (meetsRequirements) {
        await this.contributeMedicationPattern(pattern);
      }
    } catch (error) {
      // Silent catch - analytics should never break the app
      console.error('Error processing local data:', error);
    }
  }
  
  /**
   * Get medications from local storage for pattern extraction
   */
  private async getMedicationsFromLocalStorage(): Promise<MedicationData[]> {
    try {
      // Get all medication data using the getData method with no ID (returns all documents in collection)
      const medicationsData = await databaseService.getData('medications') as Record<string, unknown> | null;
      
      if (!medicationsData) {
        return [];
      }
      
      // Convert record to array of medication data
      const medications: MedicationData[] = [];
      
      // Use Object.entries to iterate over the medications record safely
      Object.entries(medicationsData).forEach(([_, data]) => {
        if (data) {
          medications.push(data as MedicationData);
        }
      });
      
      return medications;
    } catch (error) {
      console.error('Error retrieving medications:', error);
      return [];
    }
  }
  
  /**
   * Get adherence data from local storage for pattern extraction
   */
  private async getAdherenceDataFromLocalStorage(): Promise<MedicationAdherenceData[]> {
    try {
      // Get all log data using the getData method with no ID (returns all documents in collection)
      const logsData = await databaseService.getData('medication_logs') as Record<string, unknown> | null;
      
      if (!logsData) {
        return [];
      }
      
      // Convert record to array of adherence data
      const adherenceRecords: MedicationAdherenceData[] = [];
      
      // Use Object.entries to iterate over the logs record safely
      Object.entries(logsData).forEach(([_, data]) => {
        if (data) {
          adherenceRecords.push(data as MedicationAdherenceData);
        }
      });
      
      return adherenceRecords;
    } catch (error) {
      console.error('Error retrieving adherence data:', error);
      return [];
    }
  }
  
  /**
   * Contribute extracted medication pattern to analytics
   */
  private async contributeMedicationPattern(pattern: MedicationSchedulePattern): Promise<void> {
    // Only proceed if user has opted in and given general analytics consent
    if (!this.initialized || !this.consentGiven || !this.dataContributionPrefs.enabled) {
      return;
    }
    
    try {
      // Send the pattern to PostHog
      await this.trackEvent('contribute_medication_pattern', {
        pattern_id: pattern.patternId,
        medication_classes: pattern.medicationClasses,
        timing_patterns: pattern.timingPatterns,
        adherence_stats: this.dataContributionPrefs.adherenceData ? pattern.adherenceStats : undefined,
        complexity_score: pattern.complexityScore,
        // Include timestamp for pattern freshness
        generated_at: new Date().toISOString()
      });
      
      // Track this contribution locally so we don't duplicate
      await databaseService.saveData('contributed_patterns', pattern.patternId, {
        contributedAt: new Date().toISOString(),
        patternId: pattern.patternId
      });
    } catch (error) {
      console.error('Error contributing pattern:', error);
    }
  }
  
  /**
   * Process events that were saved while offline
   */
  private async processOfflineEvents(): Promise<void> {
    if (!this.initialized || !this.consentGiven || !navigator.onLine) {
      return; // Don't process if we're still offline or don't have consent
    }
    
    try {
      // Get unsent events from PouchDB
      const events = await pouchDBService.getUnsentEvents(50);
      
      if (events.length === 0) {
        return; // No events to process
      }
      
      
      
      // Send events to PostHog
      const sentEventIds: string[] = [];
      
      for (const event of events) {
        try {
          if (typeof posthog.capture === 'function') {
            posthog.capture(event.eventName, {
              ...event.properties,
              $distinct_id: event.puid,
              $time: new Date(event.timestamp).getTime() // Use original timestamp
            });
            sentEventIds.push(event._id);
          }
        } catch (error) {
          
        }
      }
      
      // Mark sent events
      if (sentEventIds.length > 0) {
        await pouchDBService.markEventsSent(sentEventIds);
        
      }
      
      // Clean up old events periodically
      await pouchDBService.cleanupSentEvents(30); // Clean up events older than 30 days
    } catch (error) {
      // Silently handle initialization errors to prevent app crashes
      // Log error in development environment only
      if (process.env.NODE_ENV === 'development') {
        console.error('PostHog initialization failed:', error);
      }
      // Mark as uninitialized so other methods can gracefully handle the failure
      this.initialized = false;
    }
  }
}

export const posthogService = new PostHogService();
